// The list that opens above an "@" in the composer. It overlays the
// transcript so the input card does not grow. Keyboard focus stays in the
// textarea; the composer moves the highlight and picks for it.
import { Fragment, type ReactNode } from 'react';

import { type EntityReferenceScope } from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import { type MentionCandidate } from '@apps/web/features/phoebe_chat_v3/api/reference_search';
import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import {
    REFERENCE_TYPE_ICON_CLASSES,
    REFERENCE_TYPE_ICONS,
} from '@apps/web/features/phoebe_chat_v3/ui/reference_icons';
import { useShiftReferenceIconClass } from '@apps/web/features/phoebe_chat_v3/ui/shift_reference_palette';
import { groupModeLabel } from '@apps/web/lib/outreach_shift_ordering';
import { cn } from '@apps/web/lib/utils';

export type MentionPickerProps = Readonly<{
    readonly candidates: ReadonlyArray<MentionCandidate>;
    readonly activeIndex: number;
    readonly loading: boolean;
    readonly onActivate: (index: number) => void;
    readonly onSelect: (candidate: MentionCandidate) => void;
    /** The outreach has more matching shifts than its section lists. */
    readonly outreachShiftsTruncated?: boolean;
    /** Lets shift rows resolve their status color outside a run harness. */
    readonly referenceScope?: EntityReferenceScope;
}>;

type PickerSection = Readonly<{
    readonly key: string;
    readonly kind: 'outreach' | MentionCandidate['type'];
    readonly label: string;
    readonly candidates: ReadonlyArray<MentionCandidate>;
}>;

const SECTION_LABELS: Readonly<Record<PickerSection['kind'], string>> = {
    outreach: 'In this outreach',
    shift: 'Shifts',
    client: 'Clients',
    caregiver: 'Caregivers',
    group: 'Groups',
};

const HEADING_CLASS =
    'text-muted-foreground m-0 px-2 pt-2 pb-1 text-v3-chat-meta font-medium tracking-[0.08em] uppercase first:pt-1';

/** Sections are runs of consecutive rows: the outreach's rows, then one run
 * per type. Cutting them from list order keeps the drawn order and the order
 * the arrow keys walk the same. */
function pickerSections(
    candidates: ReadonlyArray<MentionCandidate>,
): ReadonlyArray<PickerSection> {
    const scoped = candidates.some(
        (candidate) => candidate.section === 'outreach',
    );
    const sections: Array<PickerSection> = [];
    for (const candidate of candidates) {
        const kind =
            candidate.section === 'outreach' ? 'outreach' : candidate.type;
        const last = sections[sections.length - 1];
        if (last?.kind === kind) {
            sections[sections.length - 1] = {
                ...last,
                candidates: [...last.candidates, candidate],
            };
            continue;
        }
        sections.push({
            key: `${sections.length}:${kind}`,
            kind,
            // Only shifts have an outreach section above them to be "other"
            // than.
            label:
                scoped && kind === 'shift'
                    ? 'Other shifts'
                    : SECTION_LABELS[kind],
            candidates: [candidate],
        });
    }
    return sections;
}

/** A shift row's icon wears its calendar status accent (matching the chip
 * the pick will insert); other entities keep their identity color. */
function CandidateIcon({
    type,
    id,
    referenceScope,
}: Readonly<{
    readonly type: ReferenceType;
    readonly id: string;
    readonly referenceScope?: EntityReferenceScope;
}>): ReactNode {
    const Icon = REFERENCE_TYPE_ICONS[type];
    const shiftIconClass = useShiftReferenceIconClass(type, id, referenceScope);
    return (
        <Icon
            aria-hidden="true"
            className={cn(
                'size-4 shrink-0',
                shiftIconClass ?? REFERENCE_TYPE_ICON_CLASSES[type],
            )}
        />
    );
}

function CandidateRow({
    candidate,
    active,
    onActivate,
    onSelect,
    referenceScope,
}: Readonly<{
    readonly candidate: MentionCandidate;
    readonly active: boolean;
    readonly onActivate: () => void;
    readonly onSelect: () => void;
    readonly referenceScope?: EntityReferenceScope;
}>): ReactNode {
    const isGroup = candidate.type === 'group';
    const mode =
        candidate.groupMode === null
            ? null
            : groupModeLabel(candidate.groupMode);
    return (
        <button
            type="button"
            role="option"
            aria-selected={active}
            // A reader who cannot see the rows under a group needs its number.
            aria-label={
                isGroup
                    ? [candidate.tag, candidate.label, candidate.detail]
                          .filter((part) => part !== null && part !== '')
                          .join(', ')
                    : undefined
            }
            // Keep focus in the textarea so the caret survives.
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={onActivate}
            onClick={onSelect}
            className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-v3-chat',
                active && 'bg-muted',
            )}
        >
            <CandidateIcon
                type={candidate.type}
                id={candidate.id}
                referenceScope={referenceScope}
            />
            <span className="min-w-0 flex-1 truncate font-medium">
                {candidate.subject}
            </span>
            {mode !== null && (
                <span className="bg-muted-foreground/10 text-muted-foreground text-v3-chat-meta shrink-0 rounded px-1 py-px font-medium">
                    {mode}
                </span>
            )}
            {!isGroup && candidate.tag !== null && (
                <span className="text-muted-foreground text-v3-chat-meta shrink-0">
                    {candidate.tag}
                </span>
            )}
            {candidate.detail !== null && (
                <span className="text-muted-foreground text-v3-chat-meta shrink-0">
                    {candidate.detail}
                </span>
            )}
        </button>
    );
}

export function MentionPicker({
    candidates,
    activeIndex,
    loading,
    onActivate,
    onSelect,
    outreachShiftsTruncated = false,
    referenceScope,
}: MentionPickerProps): ReactNode {
    const sections = pickerSections(candidates);
    const hasScoped = candidates.some(
        (candidate) => candidate.section === 'outreach',
    );
    return (
        <div
            role="listbox"
            aria-label="Mention suggestions"
            className="bg-card absolute inset-x-0 bottom-full z-20 mb-1.5 max-h-56 overflow-y-auto rounded-lg border p-1 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
        >
            {candidates.length === 0 ? (
                <p className="text-muted-foreground text-v3-chat m-0 px-2 py-1.5">
                    {loading ? 'Searching…' : 'No matches'}
                </p>
            ) : (
                <Fragment>
                    {sections.map((section) => (
                        <div
                            key={section.key}
                            role="group"
                            aria-label={section.label}
                        >
                            <p className={HEADING_CLASS}>{section.label}</p>
                            {section.candidates.map((candidate) => {
                                const index = candidates.indexOf(candidate);
                                return (
                                    <CandidateRow
                                        key={`${candidate.type}:${candidate.id}`}
                                        candidate={candidate}
                                        active={index === activeIndex}
                                        onActivate={() => onActivate(index)}
                                        onSelect={() => onSelect(candidate)}
                                        referenceScope={referenceScope}
                                    />
                                );
                            })}
                            {section.kind === 'outreach' &&
                                outreachShiftsTruncated && (
                                    <p className="text-muted-foreground text-v3-chat-meta m-0 px-2 py-1">
                                        Type to filter
                                    </p>
                                )}
                        </div>
                    ))}
                    {/* The outreach rows land first; the org-wide sections
                        arrive under them. */}
                    {loading && hasScoped && (
                        <p className="text-muted-foreground text-v3-chat m-0 px-2 py-1.5">
                            Searching…
                        </p>
                    )}
                </Fragment>
            )}
        </div>
    );
}
